// src/studio/components/InteractionDialog.jsx
//
// Rebuilt on shared/ui in place of
// src/components/RDS/InteractionUploadDialog.jsx (raw fixed-position markup,
// window.confirm/alert) for the same reason as NewProjectModal: no module
// CSS in the new studio (plan D-4).
import { useState } from "react";
import PropTypes from "prop-types";
import { Modal, Tabs, FormField, Input, Select, DatePicker, FileDropzone, Button, Alert, useDiscardConfirm } from "../../shared/ui";
import { studioInteractionsApi } from "../api/studioApi";

const TYPE_OPTIONS = [
  { value: "meeting_notes", label: "Meeting Notes" },
  { value: "email_thread", label: "Email Thread" },
  { value: "slack_teams_export", label: "Slack / Teams Export" },
  { value: "workshop_summary", label: "Workshop Summary" },
  { value: "client_feedback", label: "Client Feedback" },
  { value: "other", label: "Other" },
];

const EMPTY = { title: "", interactionType: "meeting_notes", sourceDate: "", participants: "" };

export default function InteractionDialog({ open, projectId, onClose, onSuccess }) {
  const [mode, setMode] = useState("upload");
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [rawText, setRawText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);

  function reset() {
    setForm(EMPTY);
    setFiles([]);
    setRawText("");
    setError(null);
    setMode("upload");
    setDirty(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  const { requestClose, confirmDialog } = useDiscardConfirm(dirty, handleClose);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
  };

  async function handleSave() {
    if (!form.title.trim()) { setError("Title is required."); return; }
    if (mode === "upload" && files.length === 0) { setError("Add at least one file."); return; }
    if (mode === "paste" && !rawText.trim()) { setError("Paste some text first."); return; }

    setSaving(true);
    setError(null);
    try {
      const participants = form.participants
        ? form.participants.split(",").map((p) => p.trim()).filter(Boolean)
        : null;

      if (mode === "upload") {
        const body = new FormData();
        body.append("title", form.title);
        body.append("interaction_type", form.interactionType);
        if (form.sourceDate) body.append("source_date", form.sourceDate);
        if (participants) body.append("participants", participants.join(","));
        files.forEach((f) => body.append("files", f));
        await studioInteractionsApi.uploadFiles(projectId, body);
      } else {
        await studioInteractionsApi.pasteText(projectId, {
          title: form.title,
          interaction_type: form.interactionType,
          raw_text: rawText,
          source_date: form.sourceDate || null,
          participants,
        });
      }
      reset();
      onSuccess();
    } catch (err) {
      setError(err?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Modal
        open={open}
        title="Add Conversation Context"
        onClose={requestClose}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>Save</Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <Tabs
            tabs={[{ key: "upload", label: "Upload Files" }, { key: "paste", label: "Paste Text" }]}
            active={mode}
            onChange={setMode}
          />

          {error && <Alert variant="danger">{error}</Alert>}

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Title" required>
              <Input placeholder="e.g. Kickoff Meeting" value={form.title} onChange={(e) => set("title", e.target.value)} />
            </FormField>
            <FormField label="Type">
              <Select options={TYPE_OPTIONS} value={form.interactionType} onChange={(e) => set("interactionType", e.target.value)} />
            </FormField>
            <FormField label="Date" helperText="Optional">
              <DatePicker value={form.sourceDate} onChange={(iso) => set("sourceDate", iso)} />
            </FormField>
            <FormField label="Participants" helperText="Comma separated, optional">
              <Input placeholder="John, Jane, Client" value={form.participants} onChange={(e) => set("participants", e.target.value)} />
            </FormField>
          </div>

          {mode === "upload" ? (
            <FormField label="Files" required>
              <FileDropzone
                multiple
                accept={[".pdf", ".docx", ".txt", ".eml", ".msg"]}
                files={files}
                onChange={(next) => {
                  setFiles(next);
                  setDirty(true);
                }}
              />
            </FormField>
          ) : (
            <FormField label="Raw text" required>
              <textarea
                rows={6}
                value={rawText}
                onChange={(e) => {
                  setRawText(e.target.value);
                  setDirty(true);
                }}
                placeholder="Paste meeting notes or an email transcript here..."
                className="shui-input adai-form-input resize-none"
              />
            </FormField>
          )}
        </div>
      </Modal>
      {confirmDialog}
    </>
  );
}

InteractionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
